import type { QuestionOutlineItem } from '@/contracts/question-groups'
import type { FormPreviewSnapshot, FormPreviewSource } from '@/contracts/form-preview'
import type {
  FormDetailContract,
  FormFolderContract,
  FormFolderListResult,
  ListFormsParams,
  ListFormsResult,
  QuestionGroupUiContract,
} from '@/contracts/questionnaire'
import {
  duplicateMockForm,
  getFillableMockForm,
  getMockFormDetail,
  getMockFormPreviewSnapshot,
  getMockWorkspaceId,
  getPersistentFormListState,
  listMockFormFolders,
  listMockForms,
  previewMockFormPublish,
  saveMockFormDetailSnapshot,
  saveMockFormListState,
  updateMockFormStatus,
} from '@/lib/mock-api/forms'
import type { Form, FormStatus, LogicJump, Question } from '@/types/form'
import {
  apiOrMock,
  buildQuery,
  fireAndForgetApi,
  metaWithSource,
  requestApiEnvelope,
  toSourceMeta,
} from './http'
import { isApiDataClientMode } from './mode'

export { getMockWorkspaceId as getWorkspaceId, getPersistentFormListState }

export async function listForms(params: ListFormsParams = {}): Promise<ListFormsResult> {
  return apiOrMock({
    label: 'listForms',
    api: async () => {
      const envelope = await requestApiEnvelope<ListFormsResult['data']>(
        `/api/forms${buildQuery(params)}`,
      )
      return {
        data: envelope.data,
        meta: metaWithSource(envelope.meta),
      }
    },
    mock: () => listMockForms(params),
  })
}

export async function listFormFolders(): Promise<FormFolderListResult> {
  return apiOrMock({
    label: 'listFormFolders',
    api: async () => {
      const envelope = await requestApiEnvelope<FormFolderContract[]>('/api/form-folders')
      return {
        data: envelope.data,
        meta: toSourceMeta(envelope.meta),
      }
    },
    mock: listMockFormFolders,
  })
}

export async function createFormFolder(input: {
  name: string
  color?: string
  formIds?: string[]
}): Promise<FormFolderContract> {
  const envelope = await requestApiEnvelope<FormFolderContract>('/api/form-folders', {
    method: 'POST',
    body: JSON.stringify(input),
  })
  return envelope.data
}

export async function updateFormFolder(
  folderId: string,
  input: {
    name?: string
    color?: string
    formIds?: string[]
  },
): Promise<FormFolderContract> {
  const envelope = await requestApiEnvelope<FormFolderContract>(`/api/form-folders/${folderId}`, {
    method: 'PATCH',
    body: JSON.stringify({
      ...(input.name === undefined ? {} : { name: input.name }),
      ...(input.color === undefined ? {} : { color: input.color }),
    }),
  })
  if (input.formIds) {
    await setFormFolderForms(folderId, input.formIds)
    return { ...envelope.data, formIds: input.formIds }
  }
  return envelope.data
}

export async function deleteFormFolder(folderId: string): Promise<{ id: string; deleted: boolean }> {
  const envelope = await requestApiEnvelope<{ id: string; deleted: boolean }>(
    `/api/form-folders/${folderId}`,
    { method: 'DELETE' },
  )
  return envelope.data
}

export async function reorderFormFolders(orderedIds: string[]): Promise<{ orderedIds: string[] }> {
  const envelope = await requestApiEnvelope<{ orderedIds: string[] }>('/api/form-folders/reorder', {
    method: 'POST',
    body: JSON.stringify({ orderedIds }),
  })
  return envelope.data
}

export async function setFormFolderForms(
  folderId: string,
  formIds: string[],
): Promise<{ folderId: string; formIds: string[] }> {
  const envelope = await requestApiEnvelope<{ folderId: string; formIds: string[] }>(
    `/api/form-folders/${folderId}/forms`,
    {
      method: 'POST',
      body: JSON.stringify({ formIds }),
    },
  )
  return envelope.data
}

export async function getFormDetail(formId: string): Promise<FormDetailContract> {
  return apiOrMock({
    label: 'getFormDetail',
    api: async () => {
      const envelope = await requestApiEnvelope<FormDetailContract>(`/api/forms/${formId}`)
      return {
        ...envelope.data,
        outline: envelope.data.outline ?? [],
        sourceMeta: toSourceMeta(envelope.meta ?? envelope.data.sourceMeta),
      }
    },
    mock: () => getMockFormDetail(formId),
  })
}

export async function getPublicForm(formId: string): Promise<FormDetailContract | null> {
  return apiOrMock({
    label: 'getPublicForm',
    api: async () => {
      const envelope = await requestApiEnvelope<FormDetailContract>(`/api/public/forms/${formId}`)
      return {
        ...envelope.data,
        outline: envelope.data.outline ?? [],
        sourceMeta: toSourceMeta(envelope.meta ?? envelope.data.sourceMeta),
      }
    },
    mock: () => getFillableMockForm(formId),
  })
}

export async function getFormPreviewSnapshot(
  formId: string,
  options: {
    source?: Extract<FormPreviewSource, 'persistent_mock' | 'public_preview'>
  } = {},
): Promise<FormPreviewSnapshot> {
  return getMockFormPreviewSnapshot(formId, options.source ?? 'persistent_mock')
}

export function saveFormListState(patch: {
  formOrder?: string[]
  deletedFormIds?: string[]
  formFolders?: FormFolderContract[]
}) {
  if (!isApiDataClientMode()) {
    return saveMockFormListState(patch)
  }

  const state = {
    formOrder: patch.formOrder ?? [],
    deletedFormIds: patch.deletedFormIds ?? [],
    formFolders: patch.formFolders ?? [],
  }

  if (patch.formFolders) {
    fireAndForgetApi('saveFormListState.folders', async () => {
      await requestApiEnvelope('/api/form-folders/reorder', {
        method: 'POST',
        body: JSON.stringify({ orderedIds: patch.formFolders?.map((folder) => folder.id) ?? [] }),
      })
      await Promise.all(
        patch.formFolders?.map((folder) =>
          requestApiEnvelope(`/api/form-folders/${folder.id}/forms`, {
            method: 'POST',
            body: JSON.stringify({ formIds: folder.formIds }),
          }),
        ) ?? [],
      )
    })
  }

  return state
}

export async function changeFormStatus(formId: string, status: FormStatus) {
  const updated = updateMockFormStatus(formId, status)

  await apiOrMock({
    label: 'changeFormStatus',
    api: async () => {
      await requestApiEnvelope(`/api/forms/${formId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      })
      return null
    },
    mock: async () => null,
  })

  return updated
}

export async function duplicateForm(formId: string) {
  if (!isApiDataClientMode()) return duplicateMockForm(formId)

  const envelope = await requestApiEnvelope<FormDetailContract>(`/api/forms/${formId}/duplicate`, {
    method: 'POST',
  })

  return {
    ...envelope.data,
    outline: envelope.data.outline ?? [],
    sourceMeta: toSourceMeta(envelope.meta ?? envelope.data.sourceMeta),
  }
}

export async function saveFormDetailSnapshot(snapshot: {
  form: Form
  questions: Question[]
  logicJumps: LogicJump[]
  groups: QuestionGroupUiContract[]
  outline?: QuestionOutlineItem[]
}) {
  const saved = saveMockFormDetailSnapshot(snapshot)

  await apiOrMock({
    label: 'saveFormDetailSnapshot',
    api: async () => {
      await requestApiEnvelope(`/api/forms/${snapshot.form.id}/builder-snapshot`, {
        method: 'PUT',
        body: JSON.stringify(snapshot),
      })
      return null
    },
    mock: async () => null,
  })

  return saved
}

export async function publishForm(formId: string) {
  return apiOrMock({
    label: 'publishForm',
    api: async () => {
      const envelope = await requestApiEnvelope(`/api/forms/${formId}/publish`, {
        method: 'POST',
      })
      return envelope.data
    },
    mock: () => previewMockFormPublish(formId),
  })
}
